import DOMPurify from 'dompurify';
import type { Config, UponSanitizeAttributeHookEvent } from 'dompurify';

// What every rule here keeps out, whatever its allowlist lets in.
const BASE_OPTIONS = {
  // DOMPurify lets data-* and aria-* attributes past ALLOWED_ATTR unless told not to; aria-* can hide or rename text.
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  // Unwrapped, a form's labels and controls would still print, a sign-in prompt in plain text.
  ADD_FORBID_CONTENTS: ['form', 'button', 'select', 'option', 'optgroup', 'textarea']
} satisfies Config;

/** Whether an image's address carries the image itself, the only kind of address the rules here keep. */
export const isInlineImage = (src: string): boolean => src.startsWith('data:image/');

// HTML draws an image with no address as its alt text, if it has one, so the text takes its place, inside a link too.
const replaceWithAltText = (image: Element) => {
  const parent = image.parentElement;
  const alt = image.getAttribute('alt');
  if (alt) image.replaceWith(alt);
  else image.remove();
  // A link the image filled alone would be left with nothing to click.
  if (parent?.nodeName === 'A' && parent.children.length === 0 && !parent.textContent?.trim()) parent.remove();
};

/**
 * A sanitizer for HTML a user writes, on a DOMPurify instance of its own, so its hooks reach no other sanitizing.
 * Whatever `options` allow, it drops data-* and aria-* attributes, takes a form out with what it holds, keeps an
 * image only when its address is a data: URL and puts any other's alt text in its place, and opens a link in a new tab
 * without telling its target where it came from.
 * `onAttribute` sees every attribute but `src`, before DOMPurify's allowlist has ruled on it.
 */
export const htmlSanitizer = (
  options: Config,
  onAttribute?: (node: Element, data: UponSanitizeAttributeHookEvent) => void
): ((html: string) => string) => {
  const purify = DOMPurify(window);
  purify.addHook('uponSanitizeAttribute', (node, data) => {
    // An image fetched from elsewhere would tell its host who read the page and when; one carried inline tells no one.
    if (data.attrName === 'src' && !isInlineImage(data.attrValue)) data.keepAttr = false;
    else onAttribute?.(node, data);
  });
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'IMG' && !node.hasAttribute('src')) replaceWithAltText(node);
    // A link opened in place would navigate away from the card it sits in.
    if (node.nodeName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  // Spread last, so no rule's options can turn the baseline off; a rule's own forbidden contents add to the baseline's.
  const config: Config = {
    ...options,
    ...BASE_OPTIONS,
    ADD_FORBID_CONTENTS: [...(options.ADD_FORBID_CONTENTS ?? []), ...BASE_OPTIONS.ADD_FORBID_CONTENTS]
  };
  return (html) => purify.sanitize(html, config);
};
